// Admin Costing view: Azure spend over time, broken down per-resource (database
// vs AI vs container, etc.), per-service or per-environment (prod, staging,
// each Lab), optionally narrowed to one environment. Preset windows
// (1/7/30/60/90 day) or a custom range, drawn as a multi-line graph with a
// per-series total table.
//
// Below it, for the same window, the model-call ledger: every model call the
// pipeline made while building the suggestions in range (ai/usage-ledger.js),
// by stage and by deployment, with tokens and a price ESTIMATE where the
// deployment is in AI_MODEL_PRICES. The Azure panel is the authority on real
// spend; the ledger says what the spend was made of.
//
// Below that, draft generation health: how many suggestions in the window
// failed to generate, by cause (token limit, timeout, AI service error,
// request rejected, other) and by account. Categories only; the stored error
// text never leaves the server.

import { Fragment, useEffect, useState, useCallback } from 'react'
import {
  Box, Typography, Stack, ToggleButton, ToggleButtonGroup, TextField, Button,
  Paper, CircularProgress, Alert, Table, TableHead, TableRow, TableCell, TableBody, Chip,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch,
} from '@mui/material'
import { fetchCost, fetchCostLedger, fetchGenerationHealth } from './api.js'
import LineChart from './LineChart.jsx'

const PRESETS = [
  { value: '1d', label: '1D' },
  { value: '7d', label: '7D' },
  { value: '30d', label: '30D' },
  { value: '60d', label: '60D' },
  { value: '90d', label: '90D' },
]

const PALETTE = [
  '#1976d2', '#9c27b0', '#2e7d32', '#ed6c02', '#d32f2f',
  '#0288d1', '#7b1fa2', '#558b2f', '#f57c00', '#c2185b',
]
const OTHER_COLOR = '#9e9e9e'

// The chart draws the biggest series and folds the rest into one "Other"
// line; the table below still lists every series.
const CHART_TOP_N = 6

// Colors are fixed here, once, so the chart and the table always agree.
function withColors(series) {
  const folded = series.length > CHART_TOP_N + 1
  return series.map((s, i) => ({ ...s, color: folded && i >= CHART_TOP_N ? OTHER_COLOR : PALETTE[i % PALETTE.length] }))
}

function chartSeries(series) {
  if (series.length <= CHART_TOP_N + 1) return series
  const rest = series.slice(CHART_TOP_N)
  const points = {}
  for (const s of rest) for (const [d, v] of Object.entries(s.points)) points[d] = (points[d] ?? 0) + v
  return [...series.slice(0, CHART_TOP_N), { key: '__other', label: `Other (${rest.length})`, color: OTHER_COLOR, points }]
}

function tokens(n) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n ?? 0)
}

function money(v, currency = 'USD') {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(v)
  } catch {
    return `$${v.toFixed(2)}`
  }
}

function pct(v) {
  return v === null || v === undefined ? '—' : `${(v * 100).toFixed(1)}%`
}

// Plain names for the server's failure causes (repositories/usage-ledger-report.js).
const CAUSE_LABEL = {
  rate_limit: 'Token limit reached (429)',
  timeout: 'Timed out',
  service_error: 'AI service error',
  request_rejected: 'AI request rejected',
  other: 'Other',
}

const ERR_TEXT = {
  cost_access_denied: 'The app identity needs the "Cost Management Reader" role on the resource group. Once granted, costs appear here.',
  cost_view_disabled: 'The cost view is disabled (COST_VIEW_ENABLED=false).',
  cost_query_failed: 'Azure Cost Management rejected the query. Check the server logs.',
  cost_rate_limited: 'Azure Cost Management is rate limiting the query. Try again in a minute.',
}

export default function CostView() {
  const [preset, setPreset] = useState('30d')
  const [custom, setCustom] = useState({ from: '', to: '' })
  const [groupBy, setGroupBy] = useState('service')
  const [env, setEnv] = useState('')
  const [logScale, setLogScale] = useState(false)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [ledger, setLedger] = useState(null)
  const [ledgerLoading, setLedgerLoading] = useState(false)
  const [ledgerError, setLedgerError] = useState(null)
  const [health, setHealth] = useState(null)
  const [healthLoading, setHealthLoading] = useState(false)
  const [healthError, setHealthError] = useState(null)
  // Rows in "Days with failures" (a day, or '__total') expanded to show their
  // accounts. Collapsed by default.
  const [openDays, setOpenDays] = useState(() => new Set())
  const toggleDay = (day) => setOpenDays((prev) => {
    const next = new Set(prev)
    if (next.has(day)) next.delete(day)
    else next.add(day)
    return next
  })

  // The panels load independently: the Azure query can be denied or delayed
  // without taking the ledger or generation health (our own DB) down with it,
  // and vice versa.
  const load = useCallback(async (params) => {
    setLoading(true)
    setError(null)
    setLedgerLoading(true)
    setLedgerError(null)
    setHealthLoading(true)
    setHealthError(null)
    const azure = fetchCost({ ...params, groupBy, env: env || null })
      .then((d) => setData(d))
      .catch((e) => { setError(e.code || e.message); setData(null) })
      .finally(() => setLoading(false))
    const led = fetchCostLedger(params)
      .then((d) => setLedger(d))
      .catch((e) => { setLedgerError(e.code || e.message); setLedger(null) })
      .finally(() => setLedgerLoading(false))
    const gen = fetchGenerationHealth(params)
      .then((d) => setHealth(d))
      .catch((e) => { setHealthError(e.code || e.message); setHealth(null) })
      .finally(() => setHealthLoading(false))
    await Promise.all([azure, led, gen])
  }, [groupBy, env])

  // Reload on preset/groupBy/env change (custom range loads via the Apply
  // button). The server folds every grouping from one cached Azure answer per
  // window, so these reloads do not each cost a Cost Management query.
  useEffect(() => {
    if (preset) load({ preset })
    else if (custom.from && custom.to) load({ from: custom.from, to: custom.to })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, groupBy, env])

  const applyCustom = () => {
    if (custom.from && custom.to) { setPreset(null); load({ from: custom.from, to: custom.to }) }
  }

  // Only days that had a failure, newest first.
  const failureDays = health ? health.byDay.filter((d) => d.failed > 0).reverse() : []

  const currency = data?.currency ?? 'USD'
  const colored = data ? withColors(data.series) : []
  const envOptions = data?.environments ?? []
  const nameColumn = groupBy === 'resource' ? 'Resource' : groupBy === 'environment' ? 'Environment' : 'Service'

  return (
    <Box sx={{ flex: 1, p: 3, overflowY: 'auto' }}>
      <Typography variant="h6" gutterBottom>Resource costs</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Actual daily Azure spend for the CXHub resource group. Resources are sorted into environments by name.
        The database server, registry, network and monitoring serve every environment, so they count as Shared.
      </Typography>

      <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center" sx={{ mb: 2, rowGap: 1.5 }}>
        <ToggleButtonGroup size="small" exclusive value={preset} onChange={(_e, v) => { if (v) setPreset(v) }}>
          {PRESETS.map((p) => <ToggleButton key={p.value} value={p.value}>{p.label}</ToggleButton>)}
        </ToggleButtonGroup>

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
            value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
            value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} />
          <Button size="small" variant="outlined" onClick={applyCustom} disabled={!custom.from || !custom.to}>Apply</Button>
        </Stack>

        <Box sx={{ flexGrow: 1 }} />

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="cost-env-label">Environment</InputLabel>
          <Select labelId="cost-env-label" label="Environment" value={env} onChange={(e) => setEnv(e.target.value)}>
            <MenuItem value="">All environments</MenuItem>
            {/* Keep the chosen one listed even while the reload is in flight. */}
            {env && !envOptions.some((o) => o.key === env) && <MenuItem value={env}>{env}</MenuItem>}
            {envOptions.map((o) => (
              <MenuItem key={o.key} value={o.key}>{o.label} · {money(o.total, currency)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <ToggleButtonGroup size="small" exclusive value={groupBy} onChange={(_e, v) => { if (v) setGroupBy(v) }}>
          <ToggleButton value="environment">By environment</ToggleButton>
          <ToggleButton value="resource">By resource</ToggleButton>
          <ToggleButton value="service">By service</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {error && <Alert severity={error === 'cost_access_denied' ? 'warning' : 'error'} sx={{ mb: 2 }}>{ERR_TEXT[error] ?? error}</Alert>}

      <Paper variant="outlined" sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : data ? (
          <>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {data.from} → {data.to}
                {data.cachedAt && ` · cached ${new Date(data.cachedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`}
                {data.stale && ' (Azure is rate limiting; showing the last successful load)'}
              </Typography>
              <Typography variant="h6">{money(data.grandTotal ?? 0, currency)}<Typography component="span" variant="body2" color="text.secondary"> total</Typography></Typography>
            </Stack>
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Cost data is delayed — Azure Cost Management lags ~8–24h and the current day is partial. Recent figures will keep filling in.
              {' '}Until the non-prod AI account went live, staging, the Labs and the clone job billed their AI use to the prod AI account, so earlier prod AI spend includes theirs.
            </Alert>
            <Stack direction="row" justifyContent="flex-end">
              <FormControlLabel
                control={<Switch size="small" checked={logScale} onChange={(e) => setLogScale(e.target.checked)} />}
                label={<Typography variant="body2">Log scale</Typography>}
              />
            </Stack>
            <LineChart dates={data.dates} series={chartSeries(colored)} valueFmt={(v) => money(v, currency)} responsive logScale={logScale} />

            <Table size="small" sx={{ mt: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{nameColumn}</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {colored.map((s) => (
                  <TableRow key={s.key}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color }} />
                        <span>{s.label}</span>
                      </Stack>
                    </TableCell>
                    <TableCell>{s.service ? <Chip size="small" variant="outlined" label={s.service} /> : '—'}</TableCell>
                    <TableCell align="right">{money(s.total, currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        ) : null}
      </Paper>

      <Typography variant="h6" sx={{ mt: 4 }} gutterBottom>Model call ledger</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What the AI spend above was made of: every model call recorded on the suggestions built in this window, by pipeline stage and by deployment.
        Cost here is an estimate from configured token prices; the Azure figures above are the actual bill.
      </Typography>

      {ledgerError && <Alert severity="error" sx={{ mb: 2 }}>{ledgerError === 'ledger_query_failed' ? 'The ledger query failed. Check the server logs.' : ledgerError}</Alert>}

      <Paper variant="outlined" sx={{ p: 2 }}>
        {ledgerLoading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : ledger ? (
          <>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {ledger.from} → {ledger.to} · {tokens(ledger.suggestions.measured)} suggestions measured
                {ledger.suggestions.unmeasured > 0 ? ` · ${tokens(ledger.suggestions.unmeasured)} predate the ledger (2026-09-18) and are not counted` : ''}
              </Typography>
              <Typography variant="h6">
                {ledger.totals.estimatedUsd === null ? 'not priced' : money(ledger.totals.estimatedUsd)}
                <Typography component="span" variant="body2" color="text.secondary"> estimated</Typography>
              </Typography>
            </Stack>
            {ledger.totals.unpricedCalls > 0 && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                {tokens(ledger.totals.unpricedCalls)} of {tokens(ledger.totals.calls)} calls ran on a deployment with no entry in AI_MODEL_PRICES, so the estimate excludes them. Their tokens are still counted.
              </Alert>
            )}
            {ledger.series.length > 0 ? (
              <>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Tokens per day, by deployment</Typography>
                <LineChart dates={ledger.dates} series={ledger.series} valueFmt={(v) => `${tokens(v)} tokens`} />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No model calls recorded in this window.</Typography>
            )}

            <Table size="small" sx={{ mt: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Stage</TableCell>
                  <TableCell>Deployment</TableCell>
                  <TableCell align="right">Calls</TableCell>
                  <TableCell align="right">From cache</TableCell>
                  <TableCell align="right">Input tokens</TableCell>
                  <TableCell align="right">of which cached</TableCell>
                  <TableCell align="right">Output tokens</TableCell>
                  <TableCell align="right">Est. cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ledger.byStage.map((r) => (
                  <TableRow key={`${r.label}|${r.model}`}>
                    <TableCell>{r.label}</TableCell>
                    <TableCell><Chip size="small" variant="outlined" label={r.model} /></TableCell>
                    <TableCell align="right">{tokens(r.calls)}</TableCell>
                    <TableCell align="right">{tokens(r.cachedCalls)}</TableCell>
                    <TableCell align="right">{tokens(r.promptTokens)}</TableCell>
                    <TableCell align="right">{tokens(r.cachedTokens)}</TableCell>
                    <TableCell align="right">{tokens(r.completionTokens)}</TableCell>
                    <TableCell align="right">{r.estimatedUsd === null ? '—' : money(r.estimatedUsd)}</TableCell>
                  </TableRow>
                ))}
                {ledger.byStage.length > 0 && (
                  <TableRow sx={{ '& td': { fontWeight: 600 } }}>
                    <TableCell>Total</TableCell>
                    <TableCell />
                    <TableCell align="right">{tokens(ledger.totals.calls)}</TableCell>
                    <TableCell align="right">{tokens(ledger.totals.cachedCalls)}</TableCell>
                    <TableCell align="right">{tokens(ledger.totals.promptTokens)}</TableCell>
                    <TableCell align="right">{tokens(ledger.totals.cachedTokens)}</TableCell>
                    <TableCell align="right">{tokens(ledger.totals.completionTokens)}</TableCell>
                    <TableCell align="right">{ledger.totals.estimatedUsd === null ? '—' : money(ledger.totals.estimatedUsd)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {ledger.replay?.runs > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                  Quality replays: {tokens(ledger.replay.runs)} runs, {money(ledger.replay.estimatedUsd)} estimated (not in the table above)
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow><TableCell>Day</TableCell><TableCell align="right">Runs</TableCell><TableCell align="right">Est. cost</TableCell></TableRow>
                  </TableHead>
                  <TableBody>
                    {ledger.replay.days.map((d) => (
                      <TableRow key={d.day}>
                        <TableCell>{d.day}</TableCell>
                        <TableCell align="right">{tokens(d.runs)}</TableCell>
                        <TableCell align="right">{money(d.usd)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </>
        ) : null}
      </Paper>

      <Typography variant="h6" sx={{ mt: 4 }} gutterBottom>Draft generation health</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        How many suggestions in this window failed to generate, and why. A token-limit rejection (429) is retried several times first,
        so this counts only drafts that still failed. The 429 alert in Teams fires on every rejection, retried or not.
      </Typography>

      {healthError && <Alert severity="error" sx={{ mb: 2 }}>{healthError === 'generation_health_query_failed' ? 'The generation health query failed. Check the server logs.' : healthError}</Alert>}

      <Paper variant="outlined" sx={{ p: 2 }}>
        {healthLoading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : health ? (
          <>
            <Stack direction="row" spacing={4} flexWrap="wrap" sx={{ mb: 2, rowGap: 1 }}>
              {[
                ['Drafts generated', tokens(health.totals.suggestions)],
                ['Drafts failed', tokens(health.totals.failed)],
                ['Failure rate', pct(health.totals.failureRate)],
                ['Failed on token limit', tokens(health.totals.byCause.rate_limit)],
              ].map(([label, value]) => (
                <Box key={label}>
                  <Typography variant="body2" color="text.secondary">{label}</Typography>
                  <Typography variant="h6">{value}</Typography>
                </Box>
              ))}
            </Stack>

            {health.totals.suggestions > 0 ? (
              <>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Failure rate per day</Typography>
                <LineChart dates={health.dates} series={health.series} valueFmt={pct} />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No suggestions were generated in this window.</Typography>
            )}

            <Typography variant="subtitle2" sx={{ mt: 3, mb: 0.5 }}>Days with failures</Typography>
            {failureDays.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Day / Account</TableCell>
                    <TableCell align="right">Drafts</TableCell>
                    <TableCell align="right">Failed</TableCell>
                    <TableCell align="right">Failure rate</TableCell>
                    {health.causes.map((c) => <TableCell key={c} align="right">{CAUSE_LABEL[c] ?? c}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    // The window's totals first: the same columns as a day, and it
                    // expands to every account's totals for the whole window.
                    { key: '__total', label: 'Total', h: health.totals, accounts: health.byAccount, total: true },
                    ...failureDays.map((d) => ({ key: d.day, label: d.day, h: d, accounts: d.byAccount })),
                  ].map(({ key, label, h, accounts, total }) => {
                    const open = openDays.has(key)
                    return (
                      <Fragment key={key}>
                        <TableRow hover onClick={() => toggleDay(key)} sx={{ cursor: 'pointer', '& td': { fontWeight: total ? 700 : 600 }, ...(total ? { bgcolor: 'action.hover' } : {}) }}>
                          <TableCell>
                            <Box component="span" sx={{ display: 'inline-block', width: 14, color: 'text.secondary' }}>{open ? '▾' : '▸'}</Box>
                            {label}
                          </TableCell>
                          <TableCell align="right">{tokens(h.suggestions)}</TableCell>
                          <TableCell align="right">{tokens(h.failed)}</TableCell>
                          <TableCell align="right">{pct(h.failureRate)}</TableCell>
                          {health.causes.map((c) => <TableCell key={c} align="right">{tokens(h.byCause[c])}</TableCell>)}
                        </TableRow>
                        {open && accounts.map((a) => (
                          <TableRow key={`${key}-${a.account}`} hover>
                            <TableCell sx={{ pl: 4 }}>{a.account}</TableCell>
                            <TableCell align="right">{tokens(a.suggestions)}</TableCell>
                            <TableCell align="right">{tokens(a.failed)}</TableCell>
                            <TableCell align="right">{pct(a.failureRate)}</TableCell>
                            {health.causes.map((c) => <TableCell key={c} align="right">{tokens(a.byCause[c])}</TableCell>)}
                          </TableRow>
                        ))}
                      </Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No draft failed to generate in this window.</Typography>
            )}
          </>
        ) : null}
      </Paper>
    </Box>
  )
}
